import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, AlertCircle, Loader2, MapPin } from "lucide-react";
import AuthShell from "../../components/AuthShell";
import { useAuth } from "../../context/AuthContext";
import { DELIVERY_ZONES, formatApiErrorDetail } from "../../lib/constants";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

export default function CompleteProfile() {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const [phone, setPhone] = useState(user?.phone || "");
  const [zone, setZone] = useState(user?.delivery_zone || "Dorchester");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!phone.trim()) {
      setError("Please enter your phone number.");
      return;
    }
    setSaving(true);
    try {
      await updateProfile({ name: user.name, phone: phone.trim(), delivery_zone: zone });
      navigate("/home", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full h-[52px] pl-11 pr-4 rounded-xl bg-gray-100 focus:bg-white border border-transparent focus:border-[#228B22] outline-none transition-colors";

  return (
    <AuthShell title="Almost there" subtitle={`Welcome${user?.name ? ", " + user.name : ""}! Just a couple of details before you shop.`}>
      <form onSubmit={submit} className="space-y-4" data-testid="complete-profile-form">
        {error && (
          <div data-testid="complete-profile-error" className="flex items-start gap-2 rounded-xl bg-[#FFF0F0] border border-[#FFD0D0] px-4 py-3 text-sm text-[#991B1B]">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        <div className="relative">
          <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="complete-phone-input" placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} className={field} />
        </div>
        <div>
          <label className="text-sm text-gray-500 mb-1.5 block flex items-center gap-1"><MapPin size={14} /> Delivery zone</label>
          <Select value={zone} onValueChange={setZone}>
            <SelectTrigger data-testid="complete-zone-select" className="h-[52px] rounded-xl bg-gray-100 border-transparent"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DELIVERY_ZONES.map((z) => (
                <SelectItem key={z.name} value={z.name}>{z.name} {z.charge > 0 ? `(+£${z.charge})` : "(Free)"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button data-testid="complete-profile-submit" type="submit" disabled={saving} className="w-full h-[52px] rounded-full bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold flex items-center justify-center transition-colors duration-200 disabled:opacity-60">
          {saving ? <Loader2 size={20} className="animate-spin" /> : "Start Shopping"}
        </button>
      </form>
    </AuthShell>
  );
}
